'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { BookOpen, Users, Award, ArrowRight, Star, Clock, Lock, ChevronRight, Brain, HeartPulse, Code2, Search } from 'lucide-react';

const featuredCourses = [
  {
    id: 'cat-001',
    title: 'Machine Learning Fundamentals',
    instructor: 'Dr. Marcus Chen',
    topic: 'AI & Machine Learning',
    rating: 4.8,
    students: 8421,
    duration: '12h 30m',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b9b78950-1774149821493.png',
    thumbnailAlt: 'Glowing neural network nodes interconnected on dark blue background',
    topicColor: 'bg-violet-500/15 text-violet-400 border-violet-500/25',
  },
  {
    id: 'cat-002',
    title: 'Python for Data Science: Zero to Hero',
    instructor: 'Aisha Nwosu',
    topic: 'Data Science',
    rating: 4.9,
    students: 12340,
    duration: '16h 45m',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_1f265c71a-1772065606004.png',
    thumbnailAlt: 'Green matrix code on dark terminal screen',
    topicColor: 'bg-sky-500/15 text-sky-400 border-sky-500/25',
  },
  {
    id: 'cat-003',
    title: 'Introduction to Health Informatics',
    instructor: 'Dr. Priya Sharma',
    topic: 'Health Informatics',
    rating: 4.7,
    students: 5210,
    duration: '10h 20m',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_195a6e902-1764648555298.png',
    thumbnailAlt: 'Doctor using digital tablet to review patient health records in modern hospital',
    topicColor: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  },
  {
    id: 'cat-004',
    title: 'Scratch Coding for Kids: Storytelling',
    instructor: 'Jamie Okonkwo',
    topic: 'Coding for Kids',
    rating: 4.9,
    students: 9870,
    duration: '6h 15m',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_14b6860a3-1772094479185.png',
    thumbnailAlt: 'Smiling children learning to code on colorful laptops in bright classroom',
    topicColor: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
  },
  {
    id: 'cat-005',
    title: 'Deep Learning with TensorFlow 2.x',
    instructor: 'Dr. Marcus Chen',
    topic: 'AI & Machine Learning',
    rating: 4.8,
    students: 4120,
    duration: '18h 10m',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_1535839c6-1772274626046.png',
    thumbnailAlt: 'Abstract 3D blue neural network layers with glowing connection lines',
    topicColor: 'bg-violet-500/15 text-violet-400 border-violet-500/25',
  },
  {
    id: 'cat-006',
    title: 'Clinical Data Analysis with R',
    instructor: 'Dr. Priya Sharma',
    topic: 'Health Informatics',
    rating: 4.6,
    students: 1870,
    duration: '15h 45m',
    thumbnail: 'https://images.unsplash.com/photo-1723269489159-2258b7386e67',
    thumbnailAlt: 'Medical data charts and statistical graphs displayed on computer monitor',
    topicColor: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  },
];

const categories = [
  { label: 'AI & Machine Learning', icon: <Brain size={20} />, count: 24, color: 'text-violet-400', bg: 'bg-violet-500/10 border-violet-500/20 hover:border-violet-500/40' },
  { label: 'Data Science', icon: <BookOpen size={20} />, count: 18, color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-500/20 hover:border-sky-500/40' },
  { label: 'Health Informatics', icon: <HeartPulse size={20} />, count: 15, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20 hover:border-emerald-500/40' },
  { label: 'Coding for Kids', icon: <Code2 size={20} />, count: 12, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20 hover:border-amber-500/40' },
];

const stats = [
  { value: '200+', label: 'Courses available' },
  { value: '45K+', label: 'Active learners' },
  { value: '98%', label: 'Completion rate' },
  { value: 'Free', label: 'All courses' },
];

export default function HomePage() {
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2.5">
            <AppLogo size={32} />
            <span className="font-bold text-lg text-foreground tracking-tight">EugeneTechRN</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link href="/course-catalogue" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Browse Courses</Link>
            <Link href="/community-hub" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Community</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link href="/sign-up-login-screen" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors hidden sm:block">
              Sign In
            </Link>
            <Link href="/sign-up-login-screen" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity">
              Get Started
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden pt-16 pb-20 px-4 sm:px-6 lg:px-8">
        {/* Animated gradient background */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 40%, #24243e 70%, #0f2027 100%)',
          }}
        />
        {/* Animated floating orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-1/4 w-72 h-72 rounded-full opacity-20 blur-3xl animate-pulse" style={{ background: 'radial-gradient(circle, #4F46E5, transparent)' }} />
          <div className="absolute bottom-10 right-1/4 w-96 h-96 rounded-full opacity-15 blur-3xl" style={{ background: 'radial-gradient(circle, #7C3AED, transparent)', animation: 'pulse 4s ease-in-out infinite 1s' }} />
          <div className="absolute top-1/2 left-10 w-48 h-48 rounded-full opacity-10 blur-2xl" style={{ background: 'radial-gradient(circle, #10B981, transparent)', animation: 'pulse 5s ease-in-out infinite 2s' }} />
          <div className="absolute top-1/3 right-10 w-56 h-56 rounded-full opacity-10 blur-2xl" style={{ background: 'radial-gradient(circle, #F59E0B, transparent)', animation: 'pulse 6s ease-in-out infinite 0.5s' }} />
        </div>
        {/* Subtle grid overlay */}
        <div
          className="absolute inset-0 pointer-events-none opacity-10"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Learn AI, Data Science &amp;{' '}
            <span style={{ color: '#818CF8' }}>Health Tech</span>
          </h1>
          <p className="text-lg mb-10 max-w-2xl mx-auto leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
            Browse our full catalogue for free. Create a free account to enroll in any course and start learning at your own pace.
          </p>

          {/* Search bar */}
          <div className="flex items-center gap-3 max-w-xl mx-auto mb-8">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.5)' }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                placeholder="Search courses..."
                className="w-full pl-10 pr-4 py-3 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#fff',
                }}
              />
            </div>
            <Link
              href="/course-catalogue"
              className="px-5 py-3 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity whitespace-nowrap"
              style={{ background: '#4F46E5', color: '#fff' }}
            >
              Browse All
            </Link>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap items-center justify-center gap-8">
            {stats?.map((s) => (
              <div key={s?.label} className="text-center">
                <p className="text-2xl font-bold text-white">{s?.value}</p>
                <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>{s?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl font-bold text-foreground mb-6">Browse by Category</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {categories?.map((cat) => (
              <Link
                key={cat?.label}
                href="/course-catalogue"
                className={`flex items-center gap-3 p-4 rounded-2xl border transition-all duration-200 cursor-pointer ${cat?.bg}`}
              >
                <span className={cat?.color}>{cat?.icon}</span>
                <div>
                  <p className="text-sm font-semibold text-foreground">{cat?.label}</p>
                  <p className="text-xs text-muted-foreground">{cat?.count} courses</p>
                </div>
                <ChevronRight size={14} className="text-muted-foreground ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      <section className="py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Featured Courses</h2>
              <p className="text-muted-foreground text-sm mt-1">Create a free account to enroll and start learning</p>
            </div>
            <Link href="/course-catalogue" className="text-sm text-primary font-semibold hover:underline flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredCourses?.map((course) => (
              <div key={course?.id} className="bg-card border border-border rounded-2xl overflow-hidden hover:shadow-lg transition-shadow group">
                {/* Thumbnail */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={course?.thumbnail}
                    alt={course?.thumbnailAlt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {/* Lock overlay */}
                  <div className="absolute inset-0 bg-foreground/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center gap-2 bg-card/90 px-4 py-2 rounded-full">
                      <Lock size={14} className="text-primary" />
                      <span className="text-sm font-semibold text-foreground">Enroll to Access</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full border ${course?.topicColor}`}>
                    {course?.topic}
                  </span>
                  <h3 className="font-semibold text-foreground text-sm leading-snug line-clamp-2">{course?.title}</h3>
                  <p className="text-xs text-muted-foreground">{course?.instructor}</p>

                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Star size={11} className="text-amber-400 fill-amber-400" />
                      {course?.rating}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users size={11} />
                      {course?.students?.toLocaleString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={11} />
                      {course?.duration}
                    </span>
                  </div>

                  <Link
                    href="/sign-up-login-screen"
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-primary/10 border border-primary/20 text-primary text-sm font-semibold hover:bg-primary hover:text-primary-foreground transition-all"
                  >
                    <Lock size={13} />
                    Enroll Free — Sign Up
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-card border border-border rounded-3xl p-10">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-5">
              <Award size={24} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">Ready to start learning?</h2>
            <p className="text-muted-foreground mb-8 text-sm leading-relaxed max-w-md mx-auto">
              All courses are completely free. Create your account in seconds and get instant access to your enrolled courses.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/sign-up-login-screen"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity"
              >
                Create Free Account
                <ArrowRight size={15} />
              </Link>
              <Link
                href="/course-catalogue"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-border text-foreground font-semibold text-sm hover:bg-muted transition-colors"
              >
                Browse Courses First
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <AppLogo size={24} />
            <span className="text-sm font-semibold text-foreground">EugeneTechRN</span>
          </div>
          <p className="text-xs text-muted-foreground">© 2026 EugeneTechRN. All rights reserved.</p>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <Link href="/sign-up-login-screen" className="hover:text-foreground transition-colors">Student Login</Link>
            <Link href="/admin-login" className="hover:text-foreground transition-colors">Admin Login</Link>
            <Link href="/course-catalogue" className="hover:text-foreground transition-colors">Courses</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}